import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Send, Loader2, Link2, AlertCircle } from "lucide-react";
import { isValidInstagramUrl } from "@/utils/instagram";

interface LinkInputProps {
    links: string[];
    isProcessing: boolean;
    onUpdateLink: (index: number, val: string) => void;
    onAddLink: () => void;
    onRemoveLink: (index: number) => void;
    onStartProcessing: () => void;
}

export function LinkInput({
    links,
    isProcessing,
    onUpdateLink,
    onAddLink,
    onRemoveLink,
    onStartProcessing,
}: LinkInputProps) {
    const hasValidLinks = links.some((l) => isValidInstagramUrl(l));
    const getValidationState = (link: string) => {
        if (!link.trim()) return 'empty';
        return isValidInstagramUrl(link) ? 'valid' : 'invalid';
    };

    return (
        <div className="space-y-6">
            <div className="glass border border-white/5 rounded-3xl overflow-hidden">
                <div className="flex items-center justify-between p-4 px-6 border-b border-white/5 bg-white/2">
                    <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-primary" />
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                            Instagram Links
                        </h2>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onAddLink}
                        className="h-8 px-3 rounded-full hover:bg-white/5 text-xs font-bold"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add Link
                    </Button>
                </div>

                <div className="p-4 px-6 space-y-3 max-h-[40vh] overflow-y-auto custom-scrollbar">
                    <AnimatePresence initial={false}>
                        {links.map((link, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                                animate={{ opacity: 1, height: "auto", marginBottom: 12 }}
                                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                className="flex gap-2 group"
                            >
                                <div className="relative flex-1">
                                    <Input
                                        placeholder="Paste Reel or Post link here..."
                                        value={link}
                                        onChange={(e) => onUpdateLink(idx, e.target.value)}
                                        className={`glass-input h-11 pr-10 focus:ring-primary/20 ${getValidationState(link) === 'invalid'
                                                ? 'border-destructive/50 focus:border-destructive'
                                                : ''
                                            }`}
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        {getValidationState(link) === 'invalid' ? (
                                            <AlertCircle className="w-4 h-4 text-destructive" />
                                        ) : (
                                            <span className="text-muted-foreground/30 group-focus-within:text-primary/40 transition-colors">
                                                {idx + 1}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {links.length > 1 && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => onRemoveLink(idx)}
                                        className="h-11 w-11 rounded-xl text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-all"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            </div>

            <Button
                className="w-full h-14 text-base font-bold shadow-xl shadow-primary/20 hover:shadow-primary/30 transition-all hover:scale-[1.01] active:scale-[0.98] rounded-2xl group relative overflow-hidden"
                onClick={onStartProcessing}
                disabled={isProcessing || !hasValidLinks}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                {isProcessing ? (
                    <div className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Processing your reels...</span>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <Send className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        <span>Generate GenZ Comments</span>
                    </div>
                )}
            </Button>
        </div>
    );
}
